import { useState, useEffect } from 'react';
import {
  Card,
  TextField,
  Button,
  Banner,
  BlockStack,
  InlineStack,
  Text,
  Spinner,
  DataTable,
  Modal,
  TextContainer,
  Checkbox,
} from '@shopify/polaris';
import { fetchJsonWithAuth } from '../utils/fetchWithAuth';

interface AdminSettingsProps {
  shop: string;
}

interface Settings {
  section_title: string;
  section_subtitle?: string;
  section_bg_color?: string;
  section_text_color?: string;
  section_border_color?: string;
  helper_text: string;
  disclaimer_text: string;
  button_label: string;
  button_color?: string;
  order_status_enabled?: boolean | number;
  status_rules: Array<{
    offsetDays: number;
    label: string;
    description?: string;
  }>;
}

export default function AdminSettings({ shop }: AdminSettingsProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<number | null>(null);
  const [newRule, setNewRule] = useState({ offsetDays: 0, label: '', description: '' });

  useEffect(() => {
    loadSettings();
  }, [shop]);

  const loadSettings = async () => {
    try {
      const data = await fetchJsonWithAuth(`/api/admin/settings?shop=${encodeURIComponent(shop)}`);
      setSettings(data);
    } catch (err) {
      setError('Failed to load settings. Session may have expired - redirecting...');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await fetchJsonWithAuth(`/api/admin/settings?shop=${encodeURIComponent(shop)}`, {
        method: 'PUT',
        body: JSON.stringify(settings),
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save settings. Session may have expired - redirecting...');
    } finally {
      setSaving(false);
    }
  };

  const handleRevertToDefault = () => {
    if (!settings) return;
    
    if (!confirm('Are you sure you want to revert all Order Status settings to their default values? This cannot be undone.')) {
      return;
    }

    // Default status rules (same as in OAuth callback)
    const defaultStatusRules = [
      { offsetDays: 0, label: 'Order Placed', description: 'Your order has been received and confirmed' },
      { offsetDays: 1, label: 'Address Verified', description: 'Your delivery address has been verified' },
      { offsetDays: 2, label: 'Preparing Your Order', description: 'Your order is being prepared' },
      { offsetDays: 4, label: 'Careful Packaging in Progress', description: 'Your items are being carefully packaged' },
      { offsetDays: 6, label: 'Quality Check in Progress', description: 'Final quality check before shipping' },
      { offsetDays: 8, label: 'Awaiting Courier Collection', description: 'Your package is ready for courier pickup' },
      { offsetDays: 12, label: 'On the Way to You', description: 'Your order is in transit to your address' },
      { offsetDays: 20, label: 'Delivered', description: 'Your order has been delivered' },
    ];

    setSettings({
      ...settings,
      section_title: 'Order Status',
      section_subtitle: 'Track your order in real-time',
      section_bg_color: '#fafbfb',
      section_text_color: '#202223',
      section_border_color: '#e1e3e5',
      helper_text: 'Please enter your order number and the email used for purchase.',
      disclaimer_text: '',
      button_label: 'Check Status',
      button_color: '#008060',
      status_rules: defaultStatusRules,
      order_status_enabled: true,
    });

    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setNewRule({ offsetDays: 0, label: '', description: '' });
    setRuleModalOpen(true);
  };

  const handleSaveRule = () => {
    if (!settings) return;

    const rule = {
      offsetDays: newRule.offsetDays,
      label: newRule.label,
      description: newRule.description || undefined,
    };

    if (editingRule !== null) {
      const updatedRules = [...settings.status_rules];
      updatedRules[editingRule] = rule;
      setSettings({ ...settings, status_rules: updatedRules });
    } else {
      setSettings({
        ...settings,
        status_rules: [...settings.status_rules, rule].sort((a, b) => a.offsetDays - b.offsetDays),
      });
    }

    setRuleModalOpen(false);
    setEditingRule(null);
  };

  const handleEditRule = (index: number) => {
    if (!settings) return;
    const rule = settings.status_rules[index];
    setNewRule({
      offsetDays: rule.offsetDays,
      label: rule.label,
      description: rule.description || '',
    });
    setEditingRule(index);
    setRuleModalOpen(true);
  };

  const handleDeleteRule = (index: number) => {
    if (!settings) return;
    const updatedRules = settings.status_rules.filter((_, i) => i !== index);
    setSettings({ ...settings, status_rules: updatedRules });
  };

  if (loading) {
    return (
      <Card>
        <BlockStack align="center">
          <Spinner size="small" />
        </BlockStack>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card>
        <Banner status="critical">Failed to load settings</Banner>
      </Card>
    );
  }

  const rulesTableRows = settings.status_rules.map((rule, index) => [
    rule.offsetDays.toString(),
    rule.label,
    rule.description || '-',
    <InlineStack key={index} gap="2">
      <Button size="micro" onClick={() => handleEditRule(index)}>
        Edit
      </Button>
      <Button size="micro" destructive onClick={() => handleDeleteRule(index)}>
        Delete
      </Button>
    </InlineStack>,
  ]);

  return (
    <BlockStack gap="500">
      {success && (
        <Banner status="success" onDismiss={() => setSuccess(false)}>
          Settings saved successfully
        </Banner>
      )}

      {error && (
        <Banner status="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {/* Save Settings and Revert Buttons at the top */}
      <div style={{ 
        marginBottom: '32px',
        padding: '28px',
        backgroundColor: '#f6f6f7',
        borderRadius: '10px',
        border: '3px solid #008060',
        boxShadow: '0 4px 16px rgba(0, 128, 96, 0.2)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px'
      }}>
        <Button 
          primary 
          onClick={handleSave} 
          loading={saving} 
          size="large"
          style={{
            fontSize: '22px',
            fontWeight: '900',
            padding: '22px 48px',
            minWidth: '280px',
            backgroundColor: '#008060',
            borderColor: '#008060',
            boxShadow: '0 6px 16px rgba(0, 128, 96, 0.4)',
            transform: 'scale(1.03)',
            transition: 'all 0.2s ease',
            letterSpacing: '0.5px'
          }}
        >
          💾 Save Settings
        </Button>
        <Button 
          onClick={handleRevertToDefault} 
          loading={saving}
          size="large"
          tone="critical"
          style={{
            fontSize: '22px',
            fontWeight: '900',
            padding: '22px 48px',
            minWidth: '280px',
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
            letterSpacing: '0.5px'
          }}
        >
          🔄 Revert to Default
        </Button>
      </div>

      <Card>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h2">
            Order Status Section Settings
          </Text>

          <Checkbox
            label="Enable Order Status Section"
            checked={settings.order_status_enabled === 1 || settings.order_status_enabled === true}
            onChange={(checked) => setSettings({ ...settings, order_status_enabled: checked })}
            helpText="Show or hide the order status lookup form on the customer page"
          />

          <TextField
            label="Section Title"
            value={settings.section_title}
            onChange={(value) => setSettings({ ...settings, section_title: value })}
            helpText="Main title displayed at the top"
          />

          <TextField
            label="Section Subtitle"
            value={settings.section_subtitle || ''}
            onChange={(value) => setSettings({ ...settings, section_subtitle: value })}
            helpText="Optional subtitle below the title"
          />

          <InlineStack gap="400">
            <div style={{ flex: 1 }}>
              <TextField
                label="Background Color"
                value={settings.section_bg_color || '#fafbfb'}
                onChange={(value) => setSettings({ ...settings, section_bg_color: value })}
                type="color"
                helpText="Section background"
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextField
                label="Text Color"
                value={settings.section_text_color || '#202223'}
                onChange={(value) => setSettings({ ...settings, section_text_color: value })}
                type="color"
                helpText="Text color"
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextField
                label="Border Color"
                value={settings.section_border_color || '#e1e3e5'}
                onChange={(value) => setSettings({ ...settings, section_border_color: value })}
                type="color"
                helpText="Border color"
              />
            </div>
          </InlineStack>

          <TextField
            label="Helper Text"
            value={settings.helper_text}
            onChange={(value) => setSettings({ ...settings, helper_text: value })}
            helpText="Instructions shown to customers"
            multiline={3}
          />

          <TextField
            label="Disclaimer Text"
            value={settings.disclaimer_text}
            onChange={(value) => setSettings({ ...settings, disclaimer_text: value })}
            helpText="Optional disclaimer text shown below the form"
            multiline={3}
          />

          <InlineStack gap="400">
            <div style={{ flex: 1 }}>
              <TextField
                label="Button Label"
                value={settings.button_label}
                onChange={(value) => setSettings({ ...settings, button_label: value })}
                helpText="Text on submit button"
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextField
                label="Button Color"
                value={settings.button_color || '#008060'}
                onChange={(value) => setSettings({ ...settings, button_color: value })}
                type="color"
                helpText="Button background"
              />
            </div>
          </InlineStack>

          <Banner status="info">
            <Text as="p">
              <strong>Note:</strong> Email validation is mandatory and cannot be disabled. The helper text
              will always include: "Please enter your order number and the email used for purchase."
            </Text>
          </Banner>
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between">
            <Text variant="headingMd" as="h2">
              Status Rules
            </Text>
            <Button onClick={handleAddRule}>Add Rule</Button>
          </InlineStack>

          <Text as="p" color="subdued">
            Configure time-based status milestones. Rules are evaluated based on days since order creation.
          </Text>

          {settings.status_rules.length > 0 ? (
            <DataTable
              columnContentTypes={['numeric', 'text', 'text', 'text']}
              headings={['Days Offset', 'Label', 'Description', 'Actions']}
              rows={rulesTableRows}
            />
          ) : (
            <Banner status="info">
              No status rules configured. Add rules to define order status milestones.
            </Banner>
            )}
          </BlockStack>
        </Card>

      <Modal
        open={ruleModalOpen}
        onClose={() => setRuleModalOpen(false)}
        title={editingRule !== null ? 'Edit Status Rule' : 'Add Status Rule'}
        primaryAction={{
          content: 'Save',
          onAction: handleSaveRule,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setRuleModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Days Offset"
              type="number"
              value={newRule.offsetDays.toString()}
              onChange={(value) => setNewRule({ ...newRule, offsetDays: parseInt(value) || 0 })}
              helpText="Number of days after order creation when this status applies"
            />
            <TextField
              label="Status Label"
              value={newRule.label}
              onChange={(value) => setNewRule({ ...newRule, label: value })}
              helpText="Display name for this status (e.g., 'Processing', 'Shipped')"
            />
            <TextField
              label="Description"
              value={newRule.description}
              onChange={(value) => setNewRule({ ...newRule, description: value })}
              helpText="Optional description shown to customers"
              multiline={3}
            />
          </BlockStack>
          </Modal.Section>
        </Modal>
      </BlockStack>
    );
  }

